import React, { useState } from 'react';
import {
  Box,
  Button,
  Menu,
  MenuItem,
  Typography,
  Divider,
  ListItemIcon,
  Chip
} from '@mui/material';
import { Business, KeyboardArrowDown, Check } from '@mui/icons-material';
import { useAuth } from '../../context/AuthContext';
import { Company } from '../../types/auth';

const CompanySwitcher: React.FC = () => {
  const { user, selectedCompany, switchCompany } = useAuth();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleCompanySwitch = (company: Company) => {
    switchCompany(company);
    handleClose();
  };

  // Don't show for admins or if user has 1 or fewer companies
  const isAdmin = user?.role === 'admin' || user?.role === 'superadmin';
  if (isAdmin || !user?.companies || user.companies.length <= 1 || !selectedCompany) {
    return null;
  }

  return (
    <Box>
      <Button
        variant="outlined"
        onClick={handleClick}
        endIcon={<KeyboardArrowDown />}
        startIcon={<Business />}
        sx={{
          borderColor: 'rgba(255, 255, 255, 0.3)',
          color: 'white',
          '&:hover': {
            borderColor: 'rgba(255, 255, 255, 0.5)',
            backgroundColor: 'rgba(255, 255, 255, 0.1)'
          }
        }}
      >
        <Box sx={{ textAlign: 'left' }}>
          <Typography variant="body2" sx={{ fontSize: '0.75rem', opacity: 0.8 }}>
            Current Company
          </Typography>
          <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
            {selectedCompany.name}
          </Typography>
        </Box>
      </Button>

      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        PaperProps={{
          sx: {
            minWidth: 250,
            mt: 1
          }
        }}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
      >
        <Box sx={{ px: 2, py: 1 }}>
          <Typography variant="subtitle2" color="text.secondary">
            Switch Company
          </Typography>
        </Box>
        <Divider />
        
        {user.companies.map((company) => (
          <MenuItem
            key={company.id}
            onClick={() => handleCompanySwitch(company)}
            selected={company.id === selectedCompany.id}
            sx={{ py: 1.5 }}
          >
            <ListItemIcon>
              {company.id === selectedCompany.id ? (
                <Check color="primary" />
              ) : (
                <Business />
              )}
            </ListItemIcon>
            <Box sx={{ flexGrow: 1 }}>
              <Typography variant="body2">
                {company.name}
              </Typography>
              {company.id === selectedCompany.id && (
                <Chip
                  label="Current"
                  size="small"
                  color="primary"
                  variant="outlined"
                  sx={{ mt: 0.5, height: 20 }}
                />
              )}
            </Box>
          </MenuItem>
        ))}
      </Menu>
    </Box>
  );
};

export default CompanySwitcher;